import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useAclModuleList, useDeleteAclModule } from '../api';
import type { AclModuleRow } from '../types';

export function AclModuleListPage() {
  const navigate = useNavigate();
  const list = useAclModuleList();
  const del = useDeleteAclModule();
  const rows = list.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<AclModuleRow | null>(null);

  const columns = useMemo<ColumnDef<AclModuleRow>[]>(
    () => [
      {
        accessorKey: 'mid',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.mid}
          </span>
        ),
      },
      {
        accessorKey: 'mname',
        header: 'Module name',
        cell: ({ row }) => (
          <Link
            to={`/update-module/${row.original.mid}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.mname}
          </Link>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => {
          const active = row.original.status === 1;
          return (
            <Badge
              variant="outline"
              className={
                active
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  : 'border-muted-foreground/40 text-muted-foreground'
              }
            >
              {active ? 'Active' : 'Inactive'}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'requestDate',
        header: 'Created',
        size: 200,
        cell: ({ row }) =>
          row.original.requestDate
            ? formatDateTime(row.original.requestDate)
            : '—',
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{row.original.mname}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-module/${row.original.mid}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  return (
    <FillPage>
      <PageHeader
        title="Modules"
        description="Permission catalog — each entry exposes a top-level capability and its child actions."
        actions={
          <Button asChild>
            <Link to="/create-module">
              <Plus className="h-4 w-4" />
              New module
            </Link>
          </Button>
        }
      />

      <DataTable<AclModuleRow, unknown>
        tableId="acl:modules:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search modules…"
        emptyTitle="No modules"
        emptyDescription='Click "New module" to add a permission catalog entry.'
        getRowId={(row) => String(row.mid)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="acl-modules.csv"
        pageSize={25}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete module?"
        description={
          <>
            Module{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.mname}
            </span>{' '}
            will be soft-deleted. Any ACL settings that reference it will keep
            their saved permissions until you reassign them.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.mid, {
            onSuccess: (res) => {
              toast.success(res?.message ?? `Deleted ${pendingDelete.mname}`);
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the module.'),
          });
        }}
      />
    </FillPage>
  );
}
